import React from "react";
import { Link, useNavigate } from "react-router-dom";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const handleAnchorClick = (sectionId, e) => {
    e.preventDefault();

    if (window.location.pathname === "/") {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(`/#${sectionId}`);
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  };

  return (
    <footer className="bg-[#2C3E50] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12">
          {/* Left column */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <img
                src="/images/logo-type.svg"
                alt="Providence Federal Group"
                className="h-16 md:h-20 w-auto max-w-full"
              />
            </div>

            <p className="text-white/80 text-base md:text-lg leading-relaxed max-w-xl mb-6">
              A Service-Disabled Veteran-Owned Small Business delivering
              mission-critical support for federal and commercial environments.
            </p>

            <div className="flex flex-wrap gap-2.5">
              <span className="bg-white/10 text-white/85 px-3 py-1.5 rounded-full text-xs md:text-sm">
                SDVOSB Certified
              </span>
              <span className="bg-white/10 text-white/85 px-3 py-1.5 rounded-full text-xs md:text-sm">
                WOSB Certified
              </span>
              <span className="bg-white/10 text-white/85 px-3 py-1.5 rounded-full text-xs md:text-sm">
                Federal Contractor
              </span>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="text-lg md:text-xl font-medium mb-4 pb-3 border-b border-white/20">
              Quick Links
            </h3>

            <ul className="space-y-3">
              <li>
                <Link
                  to="/"
                  className="text-white/80 hover:text-white transition-colors duration-300 text-sm md:text-base"
                >
                  Home
                </Link>
              </li>

              <li>
                <a
                  href="#aboutus"
                  onClick={(e) => handleAnchorClick("about-us", e)}
                  className="text-white/80 hover:text-white transition-colors duration-300 cursor-pointer text-sm md:text-base"
                >
                  About Us
                </a>
              </li>

              <li>
                <a
                  href="#services"
                  onClick={(e) => handleAnchorClick("services", e)}
                  className="text-white/80 hover:text-white transition-colors duration-300 cursor-pointer text-sm md:text-base"
                >
                  Services
                </a>
              </li>

              <li>
                <Link
                  to="/capabilities"
                  className="text-white/80 hover:text-white transition-colors duration-300 text-sm md:text-base"
                >
                  Capabilities
                </Link>
              </li>

              <li>
                <Link
                  to="/leadership"
                  className="text-white/80 hover:text-white transition-colors duration-300 text-sm md:text-base"
                >
                  Leadership
                </Link>
              </li>

              <li>
                <Link
                  to="/career"
                  className="text-white/80 hover:text-white transition-colors duration-300 text-sm md:text-base"
                >
                  Careers
                </Link>
              </li>

              <li>
                <Link
                  to="/contact"
                  className="text-white/80 hover:text-white transition-colors duration-300 text-sm md:text-base"
                >
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact info */}
          <div>
            <h3 className="text-lg md:text-xl font-medium mb-4 pb-3 border-b border-white/20">
              Contact Information
            </h3>

            <div className="space-y-5">
              <div className="flex items-start">
                <svg
                  className="w-5 h-5 text-white/70 mr-3 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>

                <div className="text-white/80 text-sm md:text-base leading-relaxed">
                  <div className="font-medium">Providence&apos;s Financial District</div>
                  <div>10 Dorrance Street Suite 700</div>
                  <div>Providence, RI 02903</div>
                  <div>United States</div>
                </div>
              </div>

              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-white/70 mr-3 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 4h4l2 5-2.5 2.5a12 12 0 005 5L15 14l5 2v4a2 2 0 01-2 2C9.163 22 2 14.837 2 6a2 2 0 012-2z"
                  />
                </svg>
                <span className="text-white/80 text-sm md:text-base">
                  401-346-9778
                </span>
              </div>

              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-white/70 mr-3 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>

                <a
                  href="mailto:support@providencefederal.com"
                  className="text-white/80 hover:text-white transition-colors duration-300 text-sm md:text-base break-all"
                >
                  support@providencefederal.com
                </a>
              </div>
            </div>

            <div className="mt-6">
              <h4 className="text-white/70 text-sm font-medium mb-3">
                Follow Us
              </h4>

              <div className="flex space-x-3">
                <a
                  href="https://www.linkedin.com/company/providence-federal-group-llc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors duration-300"
                  aria-label="LinkedIn"
                >
                  <span className="text-lg">in</span>
                </a>

                <a
                  href="https://www.facebook.com/people/Providence-Federal-Group/61583416923452/#"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors duration-300"
                  aria-label="Facebook"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>

                <a
                  href="https://www.instagram.com/providencefederalgroup/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors duration-300"
                  aria-label="Instagram"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <rect width="20" height="20" x="2" y="2" rx="5" ry="5" strokeWidth="2" />
                    <circle cx="12" cy="12" r="4" strokeWidth="2" />
                    <circle cx="18" cy="6" r="1" fill="currentColor" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 md:mt-14 pt-6 border-t border-white/20">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/70 text-xs md:text-sm">
              &copy; {currentYear} Providence Federal Group LLC. All rights reserved.
            </p>

            <Link
              to="/privacy-policy"
              className="text-white/70 hover:text-white transition-colors duration-300 text-xs md:text-sm"
            >
              Privacy Policy
            </Link>
          </div>

          <div className="mt-5 text-center">
            <p className="text-white/60 text-xs max-w-3xl mx-auto leading-relaxed">
              Providence Federal Group is a certified Service-Disabled Veteran-Owned
              Small Business and Woman-Owned enterprise registered for federal
              contracting opportunities.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;